import React, { forwardRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HttpsIcon from '@mui/icons-material/Https';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import StorefrontIcon from '@mui/icons-material/Storefront';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PaymentIcon from '@mui/icons-material/Payment';
import GroupIcon from '@mui/icons-material/Group';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SettingsIcon from '@mui/icons-material/Settings';
import { useAppSelector } from 'renderer/shared/state/hooks';
import { selectUserRole } from 'renderer/features/auth/state/userSelectors';

import SellIcon from '@mui/icons-material/Sell';
// import StorefrontIcon from '@mui/icons-material/Storefront';
// import GroupIcon from '@mui/icons-material/Group';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import QrCodeIcon from '@mui/icons-material/QrCode';
import InventoryIcon from '@mui/icons-material/Inventory';

interface SideBarNavItemProps {
  open: boolean;
  selected: boolean;
  title: string;
  to: string;
  icon: React.ReactElement;
}
const SideBarNavItem = ({
  open,
  title,
  icon,
  to,
  selected,
}: SideBarNavItemProps) => {
  const listItem = (
    <ListItemButton
      component={Link}
      to={to}
      onClick={() => null}
      selected={selected}
      sx={{
        // minHeight: '64px',
        minHeight: '48px',
        '&.Mui-selected': {
          backgroundColor: '#E3F2FD', // Lighter blue background for selected item
          color: '#1976D2', // Darker blue text color for selected item
          '& .MuiListItemIcon-root': {
            color: '#1976D2', // Darker blue icon color for selected item
          },
          borderRight: '4px solid #1677ff', // Dark blue right border for selected item
        },
        '&:hover': {
          backgroundColor: '#E3F2FD', // Lighter blue background on hover
          color: '#1976D2', // Darker blue text color on hover
          '& .MuiListItemIcon-root': {
            color: '#1976D2', // Darker blue icon color on hover
          },
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: '36px' }}>
        {/* <ListItemIcon sx={{ minWidth: open ? undefined : 0 }}> */}
        {icon}
      </ListItemIcon>
      {open && <ListItemText sx={{ marginLeft: '8px' }} primary={title} />}
    </ListItemButton>
  );

  return open ? (
    listItem
  ) : (
    <Tooltip title={title} placement="right">
      {listItem}
    </Tooltip>
  );
};

interface SideBarProps {
  open: boolean;
  onToggle: () => void;
}
const SideBar = forwardRef<HTMLDivElement, SideBarProps>(
  ({ open, onToggle }, ref) => {
    const location = useLocation();
    const role = useAppSelector(selectUserRole);
    const pages = [
      {
        title: 'Store',
        icon: <StorefrontIcon />,
        path: '/',
        allowedFor: ['admin', 'user', 'public'],
      },
      {
        title: 'Dashboard',
        icon: <DashboardIcon />,
        path: '/dashboard',
        allowedFor: ['admin', 'user'],
      },

      {
        title: 'Notifications',
        icon: <NotificationsNoneIcon />,
        path: '/notifications',
        allowedFor: ['admin'],
      },
      {
        title: 'Discounts',
        icon: <LocalOfferIcon />,
        path: '/discounts',
        allowedFor: ['admin'],
      },
      {
        title: 'Reseller Codes',
        icon: <StorefrontIcon />,
        path: '/reseller-codes',
        allowedFor: ['admin'],
      },

      {
        title: 'Transactions',
        icon: <PaymentIcon />,
        path: '/transactions',
        allowedFor: ['admin'],
      },
      {
        title: 'Webhooks',
        icon: <HttpsIcon />,
        path: '/webhooks',
        allowedFor: ['admin'],
      },
      {
        title: 'Users',
        icon: <GroupIcon />,
        path: '/users',
        allowedFor: ['admin'],
      },
      {
        title: 'Settings',
        icon: <SettingsIcon />,
        path: '/settings',
        allowedFor: ['admin', 'user'],
      },
    ].filter((page) => page.allowedFor.includes(role));

    const DrawerHeader = styled(Box)(({ theme }) => ({
      // background: 'white',
      display: 'flex',
      alignItems: 'flex-start', // Align items to the top
      padding: theme.spacing(0, 1),
      ...theme.mixins.toolbar,
    }));
    const drawerWidthOpen = 230; // Width of the Drawer when open
    const drawerWidthClosed = 60; // Width of the Drawer when closed
    return (
      <Drawer
        ref={ref}
        anchor="left"
        open={open}
        variant="permanent"
        PaperProps={{
          sx: {
            // background: '#F4F5F7',
            background: 'white',
            width: open ? '240px' : '60px', // Explicitly set the width instead of 'auto'
            maxWidth: '100%', // Prevent it from exceeding the view width
            flexShrink: 0,
            transition: 'width .3s ease',
            '& .MuiDrawer-paper': {
              width: open ? 'auto' : (theme) => theme.spacing(7.5), // Use 'auto' or a theme value instead of fixed width
              boxSizing: 'border-box',
              transition: 'width .3s ease', // Smooth transition for width
            },
          },
        }}
      >
        <DrawerHeader>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              flexGrow: 1,
            }}
          >
            {open && (
              <div style={{ paddingTop: 5 }}>
                <Typography variant="h6" noWrap sx={{ fontWeight: 'bold' }}>
                  Digital Anarchy
                </Typography>
                <Typography
                  variant="subtitle1"
                  noWrap
                  sx={{ lineHeight: 1 }}
                  style={{ lineHeight: 1 }}
                >
                  License Manager
                </Typography>
              </div>
            )}
          </Box>
          <IconButton
            onClick={onToggle}
            sx={{
              alignSelf: 'flex-start', // Align the icon at the top of the container
            }}
          >
            <MenuIcon />
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          {pages.map(({ title, icon, path }) => (
            <SideBarNavItem
              open={open}
              title={title}
              icon={icon}
              to={path}
              selected={
                location.pathname === path ||
                (location.pathname.indexOf('/purchase/') > -1 &&
                  title === 'Store') ||
                (location.pathname.indexOf('/products/') > -1 &&
                  title === 'Store')
              }
              key={`sidebar-nav-item-${title}`}
            />
          ))}
        </List>
      </Drawer>
    );
  },
);
export default SideBar;
